import { useEffect, useState } from 'react';
import { api, type Adapter, type ProbeResult, type WifiStatus } from '../lib/api.ts';
import { forgetGrantedDevice, grantedDeviceName, showAllDevicesOnce } from '../lib/transports/webbluetooth.ts';

type Message = { tone: 'ok' | 'warn' | 'error' | 'info'; text: string } | null;

export function transportLabel(kind: string): string {
  return { wifi: 'Wi-Fi', serial: 'USB / Bluetooth serial', ble: 'Bluetooth LE', sim: 'Simulated' }[kind] ?? kind;
}

/**
 * Join, test and re-address a Wi-Fi adapter.
 *
 * Moved out of the connection panel, where it sat between the picker and the
 * Scan button on every visit. It matters while a reader is being set up or has
 * stopped answering, which is when somebody opens adapter settings anyway.
 */
export function WifiTools({ adapter, onAdapterChanged }: { adapter: Adapter; onAdapterChanged: () => void }) {
  const [wifi, setWifi] = useState<WifiStatus | null>(null);
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [message, setMessage] = useState<Message>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    api.wifiStatus().then(setWifi).catch(() => setWifi(null));
  }, []);

  // A different adapter's result says nothing about this one.
  useEffect(() => {
    setProbe(null);
    setMessage(null);
  }, [adapter.id]);

  const refreshWifi = () => api.wifiStatus().then(setWifi).catch(() => undefined);

  async function joinNetwork() {
    setWorking(true);
    setMessage({ tone: 'info', text: 'Joining. macOS may ask for keychain permission.' });
    try {
      const res = await api.wifiJoin(adapter.id);
      setMessage({ tone: res.ok ? 'ok' : 'error', text: res.message });
      await refreshWifi();
      if (res.ok) await runProbe();
    } catch (err) {
      setMessage({ tone: 'error', text: (err as Error).message });
    } finally {
      setWorking(false);
    }
  }

  async function runProbe() {
    setWorking(true);
    setMessage({ tone: 'info', text: 'Testing, and sweeping the subnet if the saved address is silent…' });
    try {
      const res = await api.wifiProbe(adapter.id);
      setProbe(res);
      setMessage(
        res.reachable
          ? { tone: 'ok', text: `Adapter answered in ${res.latencyMs}ms.` }
          : { tone: res.discovered.length ? 'warn' : 'error', text: res.detail },
      );
    } catch (err) {
      setMessage({ tone: 'error', text: (err as Error).message });
    } finally {
      setWorking(false);
    }
  }

  /** Adopt an address the sweep turned up, so the next scan goes straight there. */
  async function useDiscovered(host: string) {
    setWorking(true);
    try {
      await api.saveAdapter({
        id: adapter.id,
        name: adapter.name,
        transport: adapter.transport,
        host,
        port: adapter.port,
        ssid: adapter.ssid,
      });
      onAdapterChanged();
      setProbe(null);
      setMessage({ tone: 'ok', text: `Adapter address set to ${host}. Press Test adapter to confirm.` });
    } catch (err) {
      setMessage({ tone: 'error', text: (err as Error).message });
    } finally {
      setWorking(false);
    }
  }

  // Only claim we are on the adapter's network when the OS actually told us
  // the name. Otherwise the probe result is the honest signal.
  const onAdapterNetwork = Boolean(wifi?.ssidVisible && adapter.ssid && wifi.currentSsid === adapter.ssid);
  const ssidHidden = Boolean(wifi?.supported && !wifi.ssidVisible && wifi.address);

  return (
    <div className="stack">
      <div className="row wrap small">
        <span className="dim">Network</span>
        <span className="mono">{networkLabel(wifi)}</span>
        {onAdapterNetwork && <span className="pill ok"><i className="dot" />on adapter</span>}
      </div>

      <div className="row wrap small">
        <span className="dim">Target</span>
        <span className="mono">
          {adapter.host}:{adapter.port}
        </span>
        {probe && (
          <span className={`pill ${probe.reachable ? 'ok' : 'crit'}`}>
            <i className="dot" />
            {probe.reachable ? `${probe.latencyMs}ms` : 'no answer'}
          </span>
        )}
      </div>

      <div className="row wrap">
        <button onClick={joinNetwork} disabled={working || !adapter.ssid || !wifi?.supported}>
          Join network
        </button>
        <button onClick={runProbe} disabled={working}>
          Test adapter
        </button>
      </div>

      {message && <div className={`banner ${message.tone} small`}>{message.text}</div>}

      {probe && !probe.reachable && probe.discovered.length > 0 && (
        <div className="stack" style={{ gap: 6 }}>
          <span className="eyebrow" style={{ margin: 0 }}>
            Found on this network
          </span>
          {probe.discovered.map((ip) => (
            <button key={ip} className="primary" onClick={() => useDiscovered(ip)} disabled={working}>
              Use {ip}
            </button>
          ))}
        </div>
      )}

      {ssidHidden && (
        <div className="banner info small">
          macOS will not reveal the network name to a terminal process without Location Services permission, so the
          name above is hidden rather than absent. Test adapter is the reliable check.
        </div>
      )}

      {!adapter.ssid && (
        <div className="banner warn small">
          No SSID saved yet. Add the network name and key from the label on the adapter, and you will not have to type
          them again.
        </div>
      )}
      {adapter.ssid && !adapter.hasWifiKey && (
        <div className="banner warn small">SSID saved but no network key. Joining will fail on a secured network.</div>
      )}
      {wifi?.remoteBridge ? (
        <div className="banner warn small">
          This bridge is hosted, so it cannot reach an adapter on your own network — the Wi-Fi transport only works when
          the bridge runs on your machine. Use a Bluetooth reader here, or run the app locally for Wi-Fi.
        </div>
      ) : (
        !wifi?.supported && (
          <div className="banner info small">
            Assisted join is macOS-only. Join the adapter network from your system menu, then use Test adapter.
          </div>
        )
      )}
    </div>
  );
}

/**
 * Which Bluetooth reader this session is using.
 *
 * An adapter is a pathway, not a dongle. Bluetooth means "reach the car over
 * Bluetooth"; which reader is on the bench is a separate question, and somebody
 * with two of them needs to be able to answer it differently today than they
 * did yesterday. The device is only remembered for the session, because a
 * granted device cannot be re-requested silently.
 */
export function BluetoothTools() {
  const [device, setDevice] = useState<string | null>(grantedDeviceName());
  const [message, setMessage] = useState<string | null>(null);

  return (
    <div className="stack">
      <div className="device-line" style={{ marginTop: 0 }}>
        {device ? (
          <span className="small dim">
            Using <strong>{device}</strong> this session
          </span>
        ) : (
          <span className="small dim">Chrome asks which reader to use when the scan starts.</span>
        )}
        <button
          className="tiny ghost"
          onClick={() => {
            forgetGrantedDevice();
            setDevice(null);
            setMessage('The next scan will ask which reader to use.');
          }}
        >
          {device ? 'change reader' : 'choose a different reader'}
        </button>
        {/* The filtered list only covers adapters somebody thought to name.
            An NF79L is invisible through no fault of the user, so there has to
            be a way past it that does not involve editing an array. */}
        <button
          className="tiny ghost"
          onClick={() => {
            showAllDevicesOnce();
            setDevice(null);
            setMessage('The next scan will list every Bluetooth device in range, not just known readers.');
          }}
        >
          not listed?
        </button>
      </div>
      {message && <div className="banner info small">{message}</div>}
    </div>
  );
}

/** Report what we actually know, which on modern macOS is often just an address. */
function networkLabel(wifi: WifiStatus | null): string {
  if (!wifi) return 'unknown';
  if (wifi.currentSsid) return wifi.currentSsid;
  if (wifi.address) return `${wifi.address} (name hidden)`;
  return 'not joined';
}
